import { z } from 'zod';

/**
 * Nudges (migration 20261021) — two gentle, non-blocking prompts in the web app:
 *
 *  - `deadline`  — "No deadline — when is it due?" after I create a task without one;
 *  - `timeOnDone` — "How long did it take?" chips in the "Completed · Undo" toast.
 *
 * Settings hierarchy: team default → project override; my own opt-out lives in
 * my work prefs (`nudgeDeadlineOff` / `nudgeTimeOff`).  A stored `null` means
 * "use the default" at every level and is resolved at read time:
 *
 *   team default   deadline   = regular team ON, Personal OFF
 *                  timeOnDone = regular team ON when it tracks time, Personal OFF
 *   project        override ?? the team's effective value
 */

export const NUDGE_KINDS = ['deadline', 'timeOnDone'] as const;
export type NudgeKind = (typeof NUDGE_KINDS)[number];

/** A stored value: `null` = use the default of the level above. */
const Stored = z.boolean().nullable();

export const NudgeFlagsSchema = z.object({
  deadline: z.boolean(),
  timeOnDone: z.boolean(),
});
export type NudgeFlags = z.infer<typeof NudgeFlagsSchema>;

/** GET /api/teams/:teamSlug/nudges */
export const TeamNudgeSettingsSchema = z.object({
  /** What the team chose; `null` = the default. */
  stored: z.object({ deadline: Stored, timeOnDone: Stored }),
  /** The value a `null` resolves to (explains the default in the UI). */
  defaults: NudgeFlagsSchema,
  /** What the team's projects inherit. */
  effective: NudgeFlagsSchema,
  /** `team.edit` — may change these. */
  canEdit: z.boolean(),
});
export type TeamNudgeSettings = z.infer<typeof TeamNudgeSettingsSchema>;

/** GET /api/teams/:teamSlug/projects/:projectSlug/nudges */
export const ProjectNudgeSettingsSchema = z.object({
  /** The project's override; `null` = "Using team default". */
  stored: z.object({ deadline: Stored, timeOnDone: Stored }),
  /** The team's effective value (what `null` means here). */
  team: NudgeFlagsSchema,
  effective: NudgeFlagsSchema,
  /** `project.edit_any` — may change the override. */
  canEdit: z.boolean(),
});
export type ProjectNudgeSettings = z.infer<typeof ProjectNudgeSettingsSchema>;

/** PATCH body for both levels: `null` resets to the default; omitted = unchanged. */
export const UpdateNudgeSettingsSchema = z
  .object({ deadline: Stored.optional(), timeOnDone: Stored.optional() })
  .strict()
  .refine((v) => v.deadline !== undefined || v.timeOnDone !== undefined, {
    message: 'Nothing to change',
  });
export type UpdateNudgeSettingsInput = z.infer<typeof UpdateNudgeSettingsSchema>;

/** GET /api/me/nudges — the effective switches of every project I can open. */
export const MyNudgesSchema = z.object({
  projects: z.array(
    z.object({
      projectId: z.string().uuid(),
      teamKind: z.enum(['personal', 'regular']),
      deadline: z.boolean(),
      timeOnDone: z.boolean(),
    }),
  ),
});
export type MyNudges = z.infer<typeof MyNudgesSchema>;

// ── Resolution (pure; shared by the API and the web app) ────────────────────

export interface TeamNudgeInput {
  kind: 'personal' | 'regular';
  stored: { deadline: boolean | null; timeOnDone: boolean | null };
  /** `core.team_tracks_time(team)` — someone logs time here or money tracking is on. */
  tracksTime: boolean;
}

/** What a team's `null` means. Personal is quiet by default; a regular team asks. */
export function teamNudgeDefaults(t: Pick<TeamNudgeInput, 'kind' | 'tracksTime'>): NudgeFlags {
  if (t.kind === 'personal') return { deadline: false, timeOnDone: false };
  return { deadline: true, timeOnDone: t.tracksTime };
}

export function effectiveTeamNudges(t: TeamNudgeInput): NudgeFlags {
  const d = teamNudgeDefaults(t);
  return {
    deadline: t.stored.deadline ?? d.deadline,
    timeOnDone: t.stored.timeOnDone ?? d.timeOnDone,
  };
}

export function effectiveProjectNudges(
  team: NudgeFlags,
  stored: { deadline: boolean | null; timeOnDone: boolean | null },
): NudgeFlags {
  return {
    deadline: stored.deadline ?? team.deadline,
    timeOnDone: stored.timeOnDone ?? team.timeOnDone,
  };
}
